import type {
  AiReportContent,
  AiReportDetail,
  AiReportType,
  FollowerTrendPoint,
  ComparisonTableData,
  TopPost,
  SelfTopPost,
  FunnelChartData,
} from "../types/analytics"
import type {
  AmbassadorRankingItem,
  UgcComparison,
  TopUgcPost,
} from "../types/analytics/fan-user"

// ─────────────────────────────────────────────────────────────
// AIレポートのサンプル（モック）データ
//
// 当月分（翌月にならないと生成できない期間）や、プランロック時に
// 「サンプル」として表示する固定データ。すべて架空のアカウント名・
// 架空の数値で構成し、既存のレポート描画コンポーネント
// （AiReportPage / DashboardReportPanel）がそのまま描画できる構造にする。
//
// content の JSON 構造は
// app/services/analytics/ai_report/generator_service.rb の
// MOCK_RESPONSE（{ sections: [{ title, content }] }）と同一。
// ─────────────────────────────────────────────────────────────

const SAMPLE_ACCOUNT = "sample_cafe_tokyo"
const SAMPLE_COMPETITOR = "sample_bakery_osaka"

// content（AIの考察テキスト）: 4タイプ分
const CONTENT: Record<AiReportType, AiReportContent> = {
  competitor: {
    sections: [
      {
        title: "サマリー",
        content:
          `（サンプル）@${SAMPLE_ACCOUNT} は先月フォロワーを412人増やし、平均ENG率6.8%と好調を維持しました。` +
          `競合の @${SAMPLE_COMPETITOR} は投稿頻度こそ高いものの、1投稿あたりのエンゲージメントは自社が上回っています。` +
          `保存数の伸びが顕著で、レシピ・作り方系コンテンツがファン化の起点になっています。`,
      },
      {
        title: "競合と比べて強かった点",
        content:
          `**「保存される情報設計」で差がついています。**\n\n` +
          `@${SAMPLE_ACCOUNT} のリールは平均保存率が競合の約1.7倍でした。` +
          `手順を1枚目で提示し、最後にまとめカットを置く構成が、後から見返したいという保存動機を生んでいます。` +
          `競合はビジュアル訴求は強い一方、実用情報が薄く保存につながりにくい傾向でした。`,
      },
      {
        title: "競合から学べる点・Next Action",
        content:
          `- 投稿頻度: 競合は週5、自社は週3。フィードを1本増やして接触回数を底上げする\n` +
          `- リールの冒頭2秒: 競合は結論先出しが徹底されている。フックの改善余地あり\n` +
          `- コメント返信: 競合は24時間以内の返信率が高くコミュニティ感が強い。運用ルール化を推奨`,
      },
    ],
  },
  funnel: {
    sections: [
      {
        title: "サマリー",
        content:
          `（サンプル）@${SAMPLE_ACCOUNT} の先月のファネルは、リーチ（48,200）からプロフィールアクセス（3,150）への` +
          `遷移率が6.5%と健全でした。一方、プロフィール到達後のフォロー転換は8.9%にとどまり、` +
          `「知ってもらう」段階よりも「フォローの決め手」に改善余地があります。`,
      },
      {
        title: "各段階のボトルネック分析",
        content:
          `**認知〜興味は良好、意思決定で離脱しています。**\n\n` +
          `投稿リーチとエンゲージメント（いいね2,140・保存640）は前月比プラスで、コンテンツの引きは十分です。` +
          `ただしプロフィールアクセスに対するフォロー数（280）が伸び悩んでおり、` +
          `プロフィール文とハイライトの導線が弱い可能性があります。`,
      },
      {
        title: "改善のためのNext Action",
        content:
          `- プロフィール文に「何が得られるアカウントか」を一文で明記する\n` +
          `- ハイライトの先頭に「初めての方へ」を設置し回遊を促す\n` +
          `- 保存の多い投稿をピン留めし、初訪問者に価値を即伝える`,
      },
    ],
  },
  fan_user: {
    sections: [
      {
        title: "サマリー",
        content:
          `（サンプル）先月は32件のUGC（タグ付け・メンション）が集まり、前月比+18%でした。` +
          `上位アンバサダー5名で全UGCの約4割を占めており、コアファンによる発信がリーチ拡大を牽引しています。` +
          `特に @fan_hana_gohan さんの投稿は二次拡散が大きく、新規フォロー流入の起点になりました。`,
      },
      {
        title: "アンバサダーの傾向",
        content:
          `**「日常使い」を見せてくれるファンが強い影響力を持っています。**\n\n` +
          `上位のUGCは、商品単体ではなく生活シーンに溶け込んだ投稿が中心でした。` +
          `フォロワーが自分事として想像しやすく、共感によるいいね・保存が伸びています。` +
          `逆に単なる商品紹介はエンゲージメントが伸びにくい傾向でした。`,
      },
      {
        title: "ファン育成のNext Action",
        content:
          `- 上位アンバサダーの投稿を公式ストーリーで積極的にリポストする\n` +
          `- 「#${SAMPLE_ACCOUNT}のある暮らし」など生活文脈のタグを提案する\n` +
          `- 初回UGC投稿者へDMでお礼を送り、二回目の投稿につなげる`,
      },
    ],
  },
  dashboard: {
    sections: [
      {
        title: "今月の総括",
        content:
          `（サンプル）@${SAMPLE_ACCOUNT} は先月、フォロワー・エンゲージメント・UGCのすべてで前月を上回りました。` +
          `競合分析・ファネル分析・ファンユーザー分析の3レポートを統合すると、` +
          `「コアファンの発信 → 新規リーチ → 保存による再訪」という好循環が形になりつつあります。`,
      },
      {
        title: "伸びている領域",
        content:
          `**保存とUGCが同時に伸びており、ファン基盤が厚くなっています。**\n\n` +
          `レシピ・作り方系コンテンツの保存率が高く、競合に対する優位点になっています。` +
          `アンバサダー上位5名によるUGCも増加し、広告に頼らないリーチ拡大が進んでいます。`,
      },
      {
        title: "来月に向けた重点アクション",
        content:
          `- フォロー転換率の改善（プロフィール導線とハイライト整備）を最優先にする\n` +
          `- 週次の投稿本数を1本増やし、接触回数を底上げする\n` +
          `- 上位アンバサダーとの関係を強化し、UGCの継続発信を促す`,
      },
    ],
  },
}

// ─── 競合分析タブの可視化データ ──────────────────────────────

const buildFollowerTrend = (): FollowerTrendPoint[] => {
  const points: FollowerTrendPoint[] = []
  let self = 8200
  let competitor = 15400
  for (let day = 1; day <= 30; day++) {
    self += Math.round(6 + Math.sin(day / 3) * 4)
    competitor += Math.round(4 + Math.cos(day / 4) * 3)
    const dd = String(day).padStart(2, "0")
    points.push({ date: `2026-06-${dd}`, self, competitor })
  }
  return points
}

const COMPARISON_TABLE: ComparisonTableData = {
  self: {
    avg_engagement_rate: 6.8,
    posts_count: 13,
    feed_count: 7,
    reel_count: 6,
    feed_ratio: 54,
    reel_ratio: 46,
  },
  competitor: {
    avg_engagement_rate: 4.1,
    posts_count: 21,
    feed_count: 9,
    reel_count: 12,
    feed_ratio: 43,
    reel_ratio: 57,
  },
}

const COMPETITOR_TOP_POSTS: TopPost[] = [
  {
    rank: 1,
    thumbnail_url: null,
    caption: "季節限定メニューの紹介リール",
    likes: 1820,
    comments: 96,
    engagement_rate: 5.2,
    permalink: null,
  },
  {
    rank: 2,
    thumbnail_url: null,
    caption: "店内の雰囲気を伝えるフィード投稿",
    likes: 1440,
    comments: 61,
    engagement_rate: 4.4,
    permalink: null,
  },
  {
    rank: 3,
    thumbnail_url: null,
    caption: "スタッフおすすめの組み合わせ紹介",
    likes: 1180,
    comments: 48,
    engagement_rate: 3.8,
    permalink: null,
  },
]

const SELF_TOP_POSTS: SelfTopPost[] = [
  { caption: "3ステップで作る簡単レシピリール", engagement_rate: 9.1, reach: 24800, saves: 1320 },
  { caption: "作り置きに便利な保存テクまとめ", engagement_rate: 7.6, reach: 18200, saves: 980 },
  { caption: "季節の食材を使った時短メニュー", engagement_rate: 6.9, reach: 15600, saves: 720 },
]

// ─── ファネル分析タブの可視化データ ──────────────────────────

const FUNNEL_CHART_DATA: FunnelChartData = {
  post: {
    feed_count: 7,
    reel_count: 6,
    story_count: 22,
    prev_feed_count: 6,
    prev_reel_count: 5,
    prev_story_count: 18,
  },
  reach: {
    account_reach: 48200,
    prev_account_reach: 41300,
    total_story_reach: 12600,
    prev_total_story_reach: 10800,
    feed_reel_content: 35600,
    prev_feed_reel_content: 30500,
  },
  engagement: {
    total_interactions: 3420,
    prev_total_interactions: 2980,
    total_likes: 2140,
    prev_total_likes: 1870,
    total_comments: 210,
    prev_total_comments: 176,
    total_saves: 640,
    prev_total_saves: 520,
  },
  profile_access: {
    total_profile_visits: 3150,
    prev_total_profile_visits: 2760,
  },
  action: {
    website_clicks: 186,
    prev_website_clicks: 152,
    followers_change: 412,
    followers_count: 8620,
  },
}

// ─── ファンユーザー分析タブの可視化データ ────────────────────

const AMBASSADOR_RANKING: AmbassadorRankingItem[] = [
  { rank: 1, owner_ig_id: "9001", display_name: "@fan_hana_gohan", posts_count: 5, total_likes: 3200, total_comments: 148, total_engagement: 3348 },
  { rank: 2, owner_ig_id: "9002", display_name: "@daily_kitchen_yuki", posts_count: 4, total_likes: 2410, total_comments: 96, total_engagement: 2506 },
  { rank: 3, owner_ig_id: "9003", display_name: "@morning_table_mai", posts_count: 3, total_likes: 1680, total_comments: 72, total_engagement: 1752 },
  { rank: 4, owner_ig_id: "9004", display_name: "@cafe_lover_ken", posts_count: 3, total_likes: 1290, total_comments: 54, total_engagement: 1344 },
  { rank: 5, owner_ig_id: "9005", display_name: "@sweets_diary_rin", posts_count: 2, total_likes: 980, total_comments: 41, total_engagement: 1021 },
]

const UGC_COMPARISON: UgcComparison = {
  current: { total: 32, tagged: 21, mentioned: 11, likes: 9560, comments: 411 },
  prev: { total: 27, tagged: 18, mentioned: 9, likes: 7840, comments: 352 },
}

const TOP_UGC_CURRENT: TopUgcPost[] = [
  { rank: 1, post_type: "tagged", owner_ig_id: "9001", display_name: "@fan_hana_gohan", like_count: 1240, comments_count: 58, tagged_at: "2026-06-18", media_url: null, permalink: null },
  { rank: 2, post_type: "mentioned", owner_ig_id: "9002", display_name: "@daily_kitchen_yuki", like_count: 860, comments_count: 34, tagged_at: "2026-06-11", media_url: null, permalink: null },
  { rank: 3, post_type: "tagged", owner_ig_id: "9003", display_name: "@morning_table_mai", like_count: 620, comments_count: 27, tagged_at: "2026-06-24", media_url: null, permalink: null },
]

const TOP_UGC_PREV: TopUgcPost[] = [
  { rank: 1, post_type: "tagged", owner_ig_id: "9002", display_name: "@daily_kitchen_yuki", like_count: 910, comments_count: 40, tagged_at: "2026-05-14", media_url: null, permalink: null },
  { rank: 2, post_type: "tagged", owner_ig_id: "9004", display_name: "@cafe_lover_ken", like_count: 540, comments_count: 22, tagged_at: "2026-05-22", media_url: null, permalink: null },
]

// ─── モック AiReportDetail の生成 ────────────────────────────

const baseDetail = (type: AiReportType, targetMonth: string): AiReportDetail => ({
  id: -1,
  report_type: type,
  competitor_id: null,
  target_month: targetMonth,
  target_month_label: "",
  status: "completed",
  generated_at: null,
  created_at: targetMonth,
  has_enough_data: true,
  regenerable: false,
  content: JSON.stringify(CONTENT[type]),
  input_tokens: null,
  output_tokens: null,
})

/**
 * サンプル表示用の AiReportDetail を返す。
 * 既存の描画コンポーネント（チャート・比較表・ランキング等）が
 * そのまま描画できるよう、タイプごとの可視化データも含める。
 */
export const getMockAiReport = (
  type: AiReportType,
  targetMonth: string,
): AiReportDetail => {
  const detail = baseDetail(type, targetMonth)

  if (type === "competitor") {
    detail.follower_trend_data = buildFollowerTrend()
    detail.comparison_table_data = COMPARISON_TABLE
    detail.competitor_top_posts = COMPETITOR_TOP_POSTS
    detail.self_top_posts = SELF_TOP_POSTS
  } else if (type === "funnel") {
    detail.chart_data = FUNNEL_CHART_DATA
  } else if (type === "fan_user") {
    detail.ambassador_ranking = AMBASSADOR_RANKING
    detail.ugc_comparison = UGC_COMPARISON
    detail.top_ugc_posts_current = TOP_UGC_CURRENT
    detail.top_ugc_posts_prev = TOP_UGC_PREV
  }

  return detail
}

/** content（sections）のみ欲しい場合のヘルパー */
export const getMockAiReportContent = (type: AiReportType): AiReportContent =>
  CONTENT[type]
